import { config } from "@financial-advisory/config";
import { store } from "./store";
import { toStreamError } from "./streamErrors";

export interface StoredCaseResult {
  caseId: number;
  sessionId: string;
  sessionVersionId: string;
  status: "pending" | "running" | "complete" | "failed" | "abandoned";
  messageCount: number;
  error?: string;
}

export interface StoredCustomCase {
  id: number;
  situation: string;
  segment: string;
  situationSummary: string;
  expectedSuggestion: string;
  personaDetail: string;
}

export interface StoredBenchmarkRun {
  id: string;
  timestamp: number;
  maxConcurrent: number;
  maxMessages: number;
  shouldTerminateEarly?: boolean;
  caseIds: number[];
  results: StoredCaseResult[];
  customCases?: StoredCustomCase[];
}

export interface BenchmarkCaseDefinition {
  situation: string;
  segment: string;
  situation_summary: string;
  expected_suggestion: string;
  persona_detail: string;
}

export interface BenchmarkCaseInput {
  id: number;
  case_definition: BenchmarkCaseDefinition;
}

export interface BenchmarkRunRequestPayload {
  cases: BenchmarkCaseInput[];
  max_concurrent: number;
  max_messages: number;
  should_terminate_early: boolean;
}

export interface CaseStatusUpdateEvent {
  type: "case_status_update";
  case_id: number;
  status: string;
  message_count: number;
  session_id: string;
}

export interface CaseResultEvent {
  type: "case_result";
  case_id: number;
  session_id: string;
  session_version_id: string;
  status: string;
  message_count: number;
  actual_suggestions: string[];
  is_pass: boolean;
  expects_none: boolean;
  error?: string;
}

export interface RunCompleteEvent {
  type: "run_complete";
  run_id: string;
  results: Array<{
    caseId: number;
    sessionId: string;
    sessionVersionId: string;
    status: string;
    messageCount: number;
    error?: string;
    actualSuggestions: string[];
    isPass: boolean;
    expectsNone: boolean;
  }>;
  metrics: {
    accuracy: number;
    precision: number | null;
    recall: number | null;
    totalCases: number;
    passedCases: number;
    errorCount: number;
  };
  stored_run: StoredBenchmarkRun;
}

export type BenchmarkSSEEvent =
  | CaseStatusUpdateEvent
  | CaseResultEvent
  | RunCompleteEvent;

export interface BenchmarkSSECallbacks {
  onCaseStatusUpdate?: (event: CaseStatusUpdateEvent) => void;
  onCaseResult?: (event: CaseResultEvent) => void;
  onRunComplete?: (event: RunCompleteEvent) => void;
  onError?: (error: Error) => void;
  onStreamEnd?: () => void;
}

export function streamBenchmarkRun(
  payload: BenchmarkRunRequestPayload,
  callbacks: BenchmarkSSECallbacks,
  abortSignal: AbortSignal,
): void {
  const baseUrl = config.API_BASE_URL;
  const { auth } = store.getState();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (auth.accessToken) {
    headers["Authorization"] = `Bearer ${auth.accessToken}`;
  }

  fetch(`${baseUrl}/benchmarks/run`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
    signal: abortSignal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Benchmark run failed: ${response.status}`);
      }
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          streamDone = true;
          continue;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data: ")) continue;
          const jsonStr = trimmed.slice(6);
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr) as BenchmarkSSEEvent;
            switch (event.type) {
              case "case_status_update":
                callbacks.onCaseStatusUpdate?.(event);
                break;
              case "case_result":
                callbacks.onCaseResult?.(event);
                break;
              case "run_complete":
                callbacks.onRunComplete?.(event);
                break;
            }
          } catch {
            // skip malformed events
          }
        }
      }

      callbacks.onStreamEnd?.();
    })
    .catch((err) => {
      if (abortSignal.aborted) return;
      callbacks.onError?.(toStreamError(err));
    });
}
